import { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
  AddressElement,
} from "@stripe/react-stripe-js";
import type { PaymentElementProps } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import { Link } from "react-router";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@/services/auth";
import axios from "axios";
import { logger } from "@/services/logger";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Billing } from "@/api.types";
import { useTranslation } from "react-i18next";
import { countryCodeForValue } from "@/lib/countries";

interface SetupIntentResponse {
  clientSecret: string;
}

interface PaymentMethodFormProps {
  onSuccess?: () => void;
}

// Stripe's CA/US dropdown values are subdivision codes, even when the saved
// address came from a lookup that returned a full province/state name.
const STRIPE_SUBDIVISIONS: Record<string, Record<string, string>> = {
  CA: {
    AB: "Alberta",
    BC: "British Columbia",
    MB: "Manitoba",
    NB: "New Brunswick",
    NL: "Newfoundland and Labrador",
    NT: "Northwest Territories",
    NS: "Nova Scotia",
    NU: "Nunavut",
    ON: "Ontario",
    PE: "Prince Edward Island",
    QC: "Quebec",
    SK: "Saskatchewan",
    YT: "Yukon",
  },
  US: {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    DC: "District of Columbia",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
    AS: "American Samoa",
    GU: "Guam",
    MP: "Northern Mariana Islands",
    PR: "Puerto Rico",
    VI: "Virgin Islands",
  },
};

function stripeStateForValue(country: string, state: string | undefined) {
  const subdivisions = STRIPE_SUBDIVISIONS[country];
  if (!subdivisions || !state) return state;
  const code = state.trim().toUpperCase().replace(`${country}-`, "");
  if (subdivisions[code]) return code;
  const fold = (value: string) =>
    value.normalize("NFD").replace(/\p{M}/gu, "").trim().toLowerCase();
  const foldedState = fold(state);
  return (
    Object.entries(subdivisions).find(
      ([, name]) => fold(name) === foldedState
    )?.[0] ?? state
  );
}

export function PaymentMethodForm({ onSuccess }: PaymentMethodFormProps) {
  const { t } = useTranslation("app");
  const [isLoading, setIsLoading] = useState(false);
  const [readyClientSecret, setReadyClientSecret] = useState<string>();
  const [isSaved, setIsSaved] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const { customer, token, isLoading: isSessionLoading } = useSession();
  const queryClient = useQueryClient();

  const stripeCustomerId = customer?.billing?.stripeCustomerId;
  const setupIntentKey = ["paymentSetupIntent", customer?.id, stripeCustomerId];
  const setupIntent = useQuery({
    queryKey: setupIntentKey,
    enabled:
      !isSaved &&
      !isSessionLoading &&
      !!token &&
      !!customer?.id &&
      !!stripeCustomerId,
    queryFn: async ({ signal }) => {
      const response = await axios.post<SetupIntentResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/stripe/setup-intent`,
        {},
        {
          signal,
          timeout: 15_000,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.data.clientSecret?.trim()) {
        throw new Error("SetupIntent response is missing its client secret");
      }
      return response.data;
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const clientSecret = setupIntent.data?.clientSecret;
  const savedAddress = customer?.billing?.address;
  const billingCountry = countryCodeForValue(savedAddress?.country ?? "");
  // Unknown countries cannot seed Stripe's address; let the Element collect it.
  const billingAddress =
    savedAddress && billingCountry
      ? {
          line1: savedAddress.line1,
          line2: savedAddress.line2 ?? "",
          city: savedAddress.city,
          state: stripeStateForValue(billingCountry, savedAddress.province),
          postal_code: savedAddress.postalCode ?? "",
          country: billingCountry,
        }
      : undefined;

  const handleSubmit = async (event: React.FormEvent) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (
      !stripe ||
      !elements ||
      !clientSecret ||
      setupIntent.isError ||
      readyClientSecret !== clientSecret
    ) {
      // Stripe.js is not ready yet.
      // Make sure to disable form submission until Stripe.js is ready.
      toast({
        variant: "destructive",
        title: t("paymentMethodForm.unableTitle"),
        description: t("paymentMethodForm.tryLater"),
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const result = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: customer?.firstName
                ? `${customer.firstName} ${customer.lastName}`
                : "",
              phone: customer?.phone ?? "",
              email: customer?.email ?? "",
              address: billingAddress,
            },
          },
        },
      });

      if (result.error) {
        toast({
          variant: "destructive",
          title: t("paymentMethodForm.addErrorTitle"),
          description:
            result.error.message ?? t("paymentMethodForm.unknownError"),
        });
      } else {
        // Stripe has the new card but the setup_intent.succeeded webhook hasn't
        // updated our DB yet. Fetch with ?sync=true so the server reconciles
        // from Stripe inline, then seed the React Query cache so every
        // subscriber (dashboard, settings, bulk-ship) sees the new card without
        // another round-trip.
        try {
          const { data } = await axios.get<Billing>(
            `${import.meta.env.VITE_API_URL}/api/v2/customers/me/payment-methods?sync=true`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          queryClient.setQueryData(["paymentMethods", customer?.id], data);
        } catch (syncError) {
          logger.error("Error syncing payment methods:", syncError);
          // Fall back to invalidation — user may still need to refresh
          await queryClient.invalidateQueries({
            queryKey: ["paymentMethods", customer?.id],
          });
        }
        toast({
          title: t("paymentMethodForm.addedTitle"),
          description: t("paymentMethodForm.addedDescription"),
        });
        setIsSaved(true);
        queryClient.removeQueries({ queryKey: setupIntentKey, exact: true });
        onSuccess?.();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("paymentMethodForm.addErrorTitle"),
        description:
          error instanceof Error
            ? error.message
            : t("paymentMethodForm.refreshTryAgain"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSessionLoading && (!token || !customer?.id)) {
    return (
      <div className="space-y-4">
        <p role="alert">{t("paymentMethodsScreen.loginRequired")}</p>
        <Button asChild>
          <Link to="/sign-in">{t("titles.signIn")}</Link>
        </Button>
      </div>
    );
  }

  if (!isSessionLoading && !stripeCustomerId) {
    return (
      <div className="space-y-4">
        <p role="status">{t("paymentMethodForm.billingRequired")}</p>
        <Button asChild>
          <Link to="/billing">{t("paymentMethodForm.completeBilling")}</Link>
        </Button>
      </div>
    );
  }

  if (isSaved) {
    return <p role="status">{t("paymentMethodForm.addedDescription")}</p>;
  }

  if (setupIntent.isError && !isSessionLoading) {
    return (
      <div className="space-y-4">
        <p role="alert">{t("paymentMethodForm.initErrorTitle")}</p>
        <p>{t("paymentMethodForm.initErrorDescription")}</p>
        <Button
          type="button"
          onClick={() => void setupIntent.refetch()}
          disabled={setupIntent.isFetching}
        >
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  if (isSessionLoading || setupIntent.isPending || !clientSecret) {
    return (
      <div className="flex items-center justify-center p-4">
        <LoaderCircle
          role="status"
          aria-label={t("paymentMethodForm.loading")}
          className="h-5 w-5 animate-spin"
        />
      </div>
    );
  }

  const paymentElementOptions: PaymentElementProps["options"] = {
    layout: "tabs",
    business: {
      name: "TooEZ Inc.",
    },
    defaultValues: {
      billingDetails: {
        name: customer?.firstName
          ? `${customer.firstName} ${customer.lastName}`
          : "",
        phone: customer?.phone ?? "",
      },
    },
    fields: {
      billingDetails: "auto",
    },
    wallets: {
      applePay: "auto",
      googlePay: "auto",
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <AddressElement
        options={{
          mode: "billing",
          fields: {
            phone: "always",
          },
          display: {
            name: "full",
          },
          validation: {
            phone: {
              required: "always",
            },
          },
          defaultValues: {
            name: customer?.firstName
              ? `${customer.firstName} ${customer.lastName}`
              : "",
            phone: customer?.phone,
            address: billingAddress,
          },
          autocomplete: {
            mode: "automatic",
          },
        }}
      />
      <PaymentElement
        key={clientSecret}
        options={paymentElementOptions}
        onReady={() => setReadyClientSecret(clientSecret)}
      />
      <Button
        type="submit"
        className="w-full"
        disabled={
          !stripe ||
          !elements ||
          isLoading ||
          setupIntent.isFetching ||
          readyClientSecret !== clientSecret
        }
      >
        {isLoading
          ? t("paymentMethodForm.adding")
          : t("paymentMethodForm.addPaymentMethod")}
      </Button>
    </form>
  );
}
